import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  TaskOccurrence,
  useGetTaskTemplateQuery,
  useUpdateOccurrenceMutation,
} from '../../../core/api/rtk/taskTemplatesApi';
import { fetchAssetGroups, fetchAssetItems, fetchAssetTaskOccurrences } from '../actions';

export function useOccurrenceDrawer({
  refreshOpenAssetView,
}: {
  refreshOpenAssetView: () => void;
}) {
  const dispatch = useDispatch();

  const [selectedOccurrence, setSelectedOccurrence] = useState<TaskOccurrence | null>(null);
  const [occurrenceDrawerError, setOccurrenceDrawerError] = useState<string | null>(null);
  const [updateOccurrence, { isLoading: isSavingOccurrenceDrawer }] = useUpdateOccurrenceMutation();

  const parentTaskId = selectedOccurrence?.taskId ?? null;
  const hasTemplate = parentTaskId != null;

  const { data: occurrenceTemplate, isFetching: isOccurrenceTemplateLoading } =
    useGetTaskTemplateQuery(parentTaskId as number, { skip: !hasTemplate });

  const refreshAfterTaskChange = (): void => {
    dispatch(fetchAssetGroups());
    dispatch(fetchAssetItems());
    dispatch(fetchAssetTaskOccurrences());
    refreshOpenAssetView();
  };

  const handleOpenOccurrence = (occurrence: TaskOccurrence): void => {
    setOccurrenceDrawerError(null);
    setSelectedOccurrence(occurrence);
  };

  const handleCloseOccurrenceDrawer = (): void => {
    setSelectedOccurrence(null);
    setOccurrenceDrawerError(null);
  };

  // TaskDetails saves through its own mutations, so there is nothing to patch
  // on this path -- only the asset page's own lists to bring back in step.
  const handleOccurrenceSaved = (): void => {
    setSelectedOccurrence(null);
    refreshAfterTaskChange();
  };

  /** Fallback editor for an occurrence with no template behind it. */
  const handleOccurrenceDrawerSave = async (patch: Partial<TaskOccurrence>): Promise<void> => {
    if (!selectedOccurrence) return;
    setOccurrenceDrawerError(null);
    try {
      await updateOccurrence({
        occurrenceId: selectedOccurrence.id,
        notes: (patch.notes as any) ?? undefined,
        scheduleStart: (patch.scheduleStart as any) ?? undefined,
        scheduleEnd: (patch.scheduleEnd as any) ?? undefined,
        employeeId: (patch.employeeId as any) ?? undefined,
      }).unwrap();
      setSelectedOccurrence(null);
      refreshAfterTaskChange();
    } catch (e: any) {
      setOccurrenceDrawerError(e?.data || 'Failed to save occurrence');
    }
  };

  return {
    selectedOccurrence,
    hasTemplate,
    occurrenceTemplate: occurrenceTemplate ?? null,
    isOccurrenceTemplateLoading,
    occurrenceDrawerError,
    isSavingOccurrenceDrawer,
    handleOpenOccurrence,
    handleCloseOccurrenceDrawer,
    handleOccurrenceSaved,
    handleOccurrenceDrawerSave,
  };
}
